import { FC, useMemo, useState } from 'react'

import { match } from 'ts-pattern'
import {
  Text,
  SimpleModal,
  GreyGrey80,
  Primary13Regular,
  Flex
} from '@sequencehq/core-components'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { PublishQuoteModalProps } from './types'
import { ApprovalFields } from './ApprovalFields'
import { ModalAutoRejected } from './AutoRejected.modal'
import { ModalLoading } from './Loading.modal'
import {
  PUBLISH_BODY_TEST_IDS,
  PUBLISH_MODAL_COPY
} from './PublishQuoteModalV2.constants'
import { RecipientsSection } from './RecipientsSection'
import {
  PUBLISH_BUTTON_COPY,
  publishSubmitButtonText,
  publishSubmitData
} from './publishSubmit'
import { usePublishRecipients } from './usePublishRecipients'
import { usePublishBodyCopy } from '../usePublishBodyCopy'

type ModalVariant = 'PUBLISH' | 'REPUBLISH' | 'REPUBLISH_AFTER_EXPIRY'

type ModalProps = {
  title: string
  body: string
  bodyTestId: string
  buttonCopy: (typeof PUBLISH_BUTTON_COPY)[keyof typeof PUBLISH_BUTTON_COPY]
  'data-testid': string
}

export const ModalWithoutEsignatures: FC<PublishQuoteModalProps> = ({
  onClose,
  onSubmit,
  isQuoteSyncing,
  submitting,
  isApprovalRequired,
  isSimulationPending,
  isAutoRejected
}) => {
  const ctx = useCubeContext()
  const recipients = usePublishRecipients({ isApprovalRequired })
  const bodyCopy = usePublishBodyCopy()

  /**
   * A save the modal started itself - someone moving a recipient between signer
   * and cc - must not swap the list for a spinner, or they lose their place
   * mid-change. Every other save still holds the modal, because what it says is
   * worked out from the saved quote.
   */
  const isWaitingForSync = isQuoteSyncing && !recipients.hasEditedTypes

  const [message, setMessage] = useState<string>('')
  const [approvalComment, setApprovalComment] = useState<string>('')

  const variant: ModalVariant = useMemo(() => {
    const isExpired = ctx.queries.rawData.data.quote.isExpired
    const hasChanges =
      ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible

    if (isExpired && !hasChanges) {
      return 'REPUBLISH_AFTER_EXPIRY'
    }

    if (hasChanges || isExpired) {
      return 'REPUBLISH'
    }

    return 'PUBLISH'
  }, [
    ctx.queries.rawData.data.quote.isExpired,
    ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible
  ])

  const modalProps = useMemo(() => {
    if (isApprovalRequired) {
      return {
        title: PUBLISH_MODAL_COPY.approvalRequired.title,
        body: PUBLISH_MODAL_COPY.approvalRequired.body,
        bodyTestId: PUBLISH_BODY_TEST_IDS.approvalRequired,
        buttonCopy: PUBLISH_BUTTON_COPY.publish,
        'data-testid': 'modal.publishQuoteNonEsig.sendForReview'
      }
    }

    return match<ModalVariant, ModalProps>(variant)
      .with('PUBLISH', () => ({
        title: PUBLISH_MODAL_COPY.publishQuote.title,
        body: bodyCopy.publishQuote,
        bodyTestId: bodyCopy['data-testid'],
        buttonCopy: PUBLISH_BUTTON_COPY.publish,
        'data-testid': 'modal.publishQuoteNonEsig.publish'
      }))
      .with('REPUBLISH', () => ({
        title: PUBLISH_MODAL_COPY.publishChanges.title,
        body: bodyCopy.publishChanges,
        bodyTestId: bodyCopy['data-testid'],
        buttonCopy: PUBLISH_BUTTON_COPY.publishChanges,
        'data-testid': 'modal.publishQuoteNonEsig.republish'
      }))
      .with('REPUBLISH_AFTER_EXPIRY', () => ({
        title: 'Republish quote',
        body: "You're about to republish this quote. This will reset the expiry date.",
        bodyTestId: PUBLISH_BODY_TEST_IDS.expiryReset,
        buttonCopy: PUBLISH_BUTTON_COPY.republish,
        'data-testid': 'modal.publishQuoteNonEsig.republishAfterExpiry'
      }))
      .exhaustive()
  }, [variant, isApprovalRequired, bodyCopy])

  const submitButtonText = useMemo(
    () =>
      publishSubmitButtonText({
        isApprovalRequired,
        recipients,
        copy: modalProps.buttonCopy
      }),
    [isApprovalRequired, modalProps.buttonCopy, recipients]
  )

  const submittingButtonText = useMemo(() => {
    if (isApprovalRequired) {
      return PUBLISH_MODAL_COPY.approvalRequired.submittingButton
    }

    return PUBLISH_MODAL_COPY.publishQuote.submittingButton
  }, [isApprovalRequired])

  // The prediction decides what this modal says, so we wait for it rather than
  // showing one thing and swapping it under them.
  if (isWaitingForSync || isSimulationPending) {
    return <ModalLoading />
  }

  if (isAutoRejected) {
    return <ModalAutoRejected onClose={onClose} />
  }

  return (
    <SimpleModal
      data-testid={modalProps['data-testid']}
      variant="dialog"
      isOpen
      onClose={onClose}
      title={modalProps.title}
      onSubmit={async () => {
        await onSubmit(
          publishSubmitData({
            recipients,
            message,
            approvalComment,
            isApprovalRequired
          })
        )

        return true
      }}
      disableSubmitOnEnter
      dialogPosition="top"
      submitting={submitting}
      submitButtonText={submitButtonText}
      submittingButtonText={submittingButtonText}
    >
      <Flex gap="16px" flexDir="column">
        <Text
          {...Primary13Regular}
          color={GreyGrey80}
          data-testid={modalProps.bodyTestId}
        >
          {modalProps.body}
        </Text>

        {isApprovalRequired && (
          <ApprovalFields
            data-testid="modal.publishQuoteNonEsig.approvalComment"
            approvalComment={approvalComment}
            setApprovalComment={value => setApprovalComment(value)}
          />
        )}

        <RecipientsSection
          recipients={recipients}
          message={message}
          setMessage={setMessage}
          submitting={submitting}
        />
      </Flex>
    </SimpleModal>
  )
}
